import React, { forwardRef, useCallback } from 'react';
import Proptypes from 'prop-types';
import clsx from 'clsx';
import TextField from '@material-ui/core/TextField';
import { makeStyles } from '@material-ui/core/styles';
import InputAdornment from '@material-ui/core/InputAdornment';
import KeyboardArrowDown from '@material-ui/icons/KeyboardArrowDown';

const useStyles = makeStyles(theme => {
  return {
    inputRoot: {
      boxSizing: 'border-box',
      width: 120,
      padding: 1,
      border: `1px solid ${theme.palette.border}`,
      borderRadius: 4,
      height: 40,

      '&.Mui-focused': {
        padding: 0,
        border: `2px solid ${theme.palette.primary.main}`,
        '& svg': {
          right: 11,
          top: 11,
        },
      },
    },
    selectRoot: {
      width: 120,
      padding: '8px 12px',
    },
    icon: {
      zIndex: 0,
      color: props => (props.disabled ? theme.palette.common.muted : theme.palette.text.secondary),
      backgroundColor: 'rgba(69, 77, 204, 0.08)',
      position: 'absolute',
      right: 12,
      top: 12,
      height: 16,
      width: 16,
      borderRadius: 8,
      pointerEvents: 'none',
      transition: 'transform 0.25s ease-in-out',
    },
    paper: {
      boxShadow: '0px 20px 25px rgba(0, 0, 0, 0.1), 0px 0px 20px rgba(0, 0, 0, 0.1)',
      borderRadius: 8,
      marginTop: 8,
      minWidth: 200,
    },
    menuList: {
      minWidth: 200,
      maxHeight: 224,
      borderRadius: 8,
      padding: '10px 8px',
      overflow: 'auto',

      '& li:hover': {
        borderRadius: 4,
      },
    },
    errorSpace: {
      paddingBottom: theme.spacing(2),
    },
    // Start adornment filled styles
    startAdornmentRoot: {
      color: theme.palette.text.secondary,
    },
  };
});

const MultipleSelect = forwardRef(function MultipleSelect(
  {
    children,
    className,
    disabled,
    error,
    errorText,
    fullWidth,
    helpText,
    id,
    inputRef,
    label,
    name,
    onBlur,
    onChange,
    required,
    startAdornment,
    selectClassName,
    menuClassName,
    multiple,
    ...restProps
  },
  ref
) {
  const {
    icon,
    paper,
    menuList,
    errorSpace,
    selectRoot,
    startAdornmentRoot,
    inputRoot,
  } = useStyles({
    disabled,
  });

  const handleOnChange = useCallback(event => onChange && onChange(event.target.value, event), [
    onChange,
  ]);

  const handleOnBlur = useCallback(event => onBlur && onBlur(event.target.value, event), [onBlur]);

  const selectProps = {
    classes: { icon, root: selectRoot, selectClassName },
    MenuProps: {
      classes: {
        paper,
        menuClassName,
      },
      PaperProps: {
        square: true,
      },
      getContentAnchorEl: null,
      anchorOrigin: {
        vertical: 'bottom',
        horizontal: 'left',
      },
      MenuListProps: {
        className: menuList,
      },
    },
    IconComponent: KeyboardArrowDown,
    renderValue: () => '',
    multiple: Boolean(multiple),
  };

  const inputProps = { classes: { root: inputRoot }, disableUnderline: true };

  if (startAdornment) {
    inputProps.startAdornment = (
      <InputAdornment position="start" className={startAdornmentRoot}>
        {startAdornment}
      </InputAdornment>
    );
  }

  const inputLabelProps = {
    htmlFor: id,
    shrink: false,
    style: { marginTop: 10, marginLeft: 16, fontSize: 14 },
  };

  return (
    <TextField
      {...restProps}
      InputLabelProps={inputLabelProps}
      InputProps={inputProps}
      SelectProps={selectProps}
      className={clsx([className, errorText && !error && errorSpace])}
      disabled={disabled}
      error={error}
      helperText={(error && errorText) || helpText}
      inputRef={inputRef}
      label={label}
      margin="dense"
      onBlur={handleOnBlur}
      onChange={handleOnChange}
      ref={ref}
      required={required}
      select
      id={id}
      name={name}
    >
      {children}
    </TextField>
  );
});

MultipleSelect.propTypes = {
  children: Proptypes.node,
  className: Proptypes.string,
  selectClassName: Proptypes.string,
  menuClassName: Proptypes.string,
  disabled: Proptypes.bool,
  error: Proptypes.bool,
  errorText: Proptypes.string,
  fullWidth: Proptypes.bool,
  helpText: Proptypes.string,
  id: Proptypes.string.isRequired,
  inputRef: Proptypes.oneOfType([Proptypes.func, Proptypes.object]),
  label: Proptypes.string.isRequired,
  name: Proptypes.string.isRequired,
  onBlur: Proptypes.func,
  onChange: Proptypes.func.isRequired,
  required: Proptypes.bool,
  startAdornment: Proptypes.element,
  multiple: Proptypes.bool,
};

MultipleSelect.defaultProps = {
  children: null,
  className: '',
  selectClassName: '',
  menuClassName: '',
  disabled: false,
  error: false,
  errorText: '',
  fullWidth: true,
  helpText: '',
  inputRef: null,
  onBlur: null,
  required: false,
  startAdornment: null,
  multiple: true,
};

MultipleSelect.displayName = 'MultipleSelect';

export default MultipleSelect;
